import React, { useId } from 'react';
import { commasExact1Digit } from 'services/formatters';

import type useODAForm from './useODAForm';
import './oda.css';

type Props = { form: ReturnType<typeof useODAForm> };

const TOP = 20;
const BOTTOM = 172;
const LABEL_GAP = 34;
const BAR_START = 14;
const BAR_END = 94;

const ODAPriceLevels: React.FC<Props> = ({ form }) => {
  const descriptionId = `${useId()}-price-levels`;
  if (form.loading) return null;

  const levels = [
    { key: 'open', label: 'Open price', price: form.entry },
    {
      key: 'current',
      label: `Current ${form.quoteName}`,
      price: form.currentPrice,
    },
    { key: 'tp', label: 'TP', price: form.takeProfit.price },
    { key: 'sl', label: 'SL', price: form.stopLoss.price },
  ];
  const available = levels.filter(
    level => Number.isFinite(level.price) && level.price > 0,
  );
  const unavailable = levels.filter(level => !available.includes(level));
  const low = Math.min(...available.map(level => level.price));
  const high = Math.max(...available.map(level => level.price));
  const markers = available
    .sort((a, b) => b.price - a.price)
    .map(level => {
      const y =
        high === low
          ? (TOP + BOTTOM) / 2
          : TOP + ((high - level.price) / (high - low)) * (BOTTOM - TOP);
      return { ...level, y, labelY: y, x1: BAR_START, x2: BAR_END };
    });

  // Labels can move apart, but every bar stays at its actual price on the
  // shared scale. Leader lines keep close prices readable without distorting them.
  for (let index = 1; index < markers.length; index++) {
    markers[index].labelY = Math.max(
      markers[index].labelY,
      markers[index - 1].labelY + LABEL_GAP,
    );
  }
  for (let index = markers.length - 1; index >= 0; index--) {
    markers[index].labelY = Math.min(
      markers[index].labelY,
      index === markers.length - 1
        ? BOTTOM
        : markers[index + 1].labelY - LABEL_GAP,
    );
  }

  // Split coincident/near-coincident bars horizontally so no color disappears
  // behind another. Equal prices still have exactly the same vertical position.
  for (let start = 0; start < markers.length; ) {
    let end = start + 1;
    while (end < markers.length && markers[end].y - markers[end - 1].y < 5)
      end++;
    if (end - start > 1) {
      const width = (BAR_END - BAR_START) / (end - start);
      for (let index = start; index < end; index++) {
        markers[index].x1 = BAR_START + (index - start) * width;
        markers[index].x2 = BAR_START + (index - start + 1) * width - 4;
      }
    }
    start = end;
  }

  const open = markers.find(marker => marker.key === 'open');
  const ranges = [
    { key: 'tp', label: 'TP', leg: form.takeProfit, x: 32 },
    { key: 'sl', label: 'SL', leg: form.stopLoss, x: 80 },
  ].flatMap(range => {
    const target = markers.find(marker => marker.key === range.key);
    const percentage = Number(range.leg.percentageInput);
    if (
      !open ||
      !target ||
      !range.leg.percentageInput.trim() ||
      !Number.isFinite(percentage)
    ) {
      return [];
    }
    const distance = target.y - open.y;
    const direction =
      Math.sign(distance) ||
      (form.quoteName === 'bid' ? -1 : 1) * (range.key === 'tp' ? 1 : -1);
    return [
      {
        ...range,
        fromY: open.y,
        toY: target.y,
        // Keep tiny and zero ranges readable without moving their endpoints.
        labelY: Math.max(
          8,
          Math.min(
            184,
            open.y + direction * Math.max(Math.abs(distance) / 2, 12),
          ),
        ),
        text: range.leg.percentageNegative
          ? '—'
          : `${commasExact1Digit(percentage).replace(/,/g, '')}%`,
      },
    ];
  });

  return (
    <figure className="oda-price-levels">
      <figcaption>Price levels</figcaption>
      {markers.length ? (
        <svg
          viewBox="0 0 260 192"
          role="img"
          aria-label="ODA price levels"
          aria-describedby={descriptionId}
        >
          <desc id={descriptionId}>
            Higher prices appear higher. All bars use the same price scale.
            Vertical markers show TP and SL percentages from open price.
          </desc>
          <g className="oda-price-levels__grid" aria-hidden="true">
            {[TOP, 58, 96, 134, BOTTOM].map(y => (
              <line key={y} x1={BAR_START} x2={BAR_END} y1={y} y2={y} />
            ))}
            <line x1={BAR_START} x2={BAR_START} y1={TOP} y2={BOTTOM} />
          </g>
          {ranges.map(range => (
            <g
              key={range.key}
              className={`oda-price-levels__range oda-price-levels__marker--${range.key}`}
              aria-label={`${range.label} from open price: ${range.leg.percentageNegative ? 'Invalid percentage' : range.text}`}
            >
              <path
                className="oda-price-levels__range-bar"
                d={`M ${range.x - 3} ${range.fromY} H ${range.x + 3} M ${range.x} ${range.fromY} V ${range.toY} M ${range.x - 3} ${range.toY} H ${range.x + 3}`}
              />
            </g>
          ))}
          {markers.map(marker => (
            <g
              key={marker.key}
              className={`oda-price-levels__marker oda-price-levels__marker--${marker.key}`}
              aria-label={`${marker.label}: ${form.formatPrice(marker.price)}`}
              data-level={marker.key}
              data-price={marker.price}
            >
              <path
                className="oda-price-levels__connector"
                d={`M ${BAR_END + 4} ${marker.y} L 110 ${marker.labelY} L 116 ${marker.labelY}`}
              />
              <line
                className="oda-price-levels__bar"
                x1={marker.x1}
                x2={marker.x2}
                y1={marker.y}
                y2={marker.y}
              />
              <g
                className="oda-price-levels__label"
                transform={`translate(124 ${marker.labelY})`}
              >
                <text className="oda-price-levels__name" y={-7}>
                  {marker.label}
                </text>
                <text className="oda-price-levels__value" y={9}>
                  {form.formatPrice(marker.price)}
                </text>
              </g>
            </g>
          ))}
          {ranges.map(range => (
            <text
              key={range.key}
              className={`oda-price-levels__percentage oda-price-levels__marker--${range.key}${range.leg.percentageNegative ? ' oda-price-levels__percentage--negative' : ''}`}
              x={range.x}
              y={range.labelY}
              aria-hidden="true"
            >
              {range.text}
            </text>
          ))}
        </svg>
      ) : (
        <p className="oda-price-levels__empty">
          Enter valid prices to see their levels.
        </p>
      )}
      {unavailable.length > 0 && (
        <div className="oda-price-levels__unavailable">
          {unavailable.map(level => (
            <span key={level.key}>{level.label} —</span>
          ))}
        </div>
      )}
    </figure>
  );
};

export default ODAPriceLevels;
